import { useEffect, useState } from "react";
import styled from "styled-components";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { captureException } from "@sentry/react";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import Layout from "components/Layout";
import PageTitle from "components/text/PageTitle";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import ModalWrap from "components/other/ModalWrap";
import Backdrop from "components/other/Backdrop";
import ModalWindow from "components/other/ModalWindow";
import ButtonPrimary from "components/buttons/ButtonPrimary";
import ButtonWhite from "components/buttons/ButtonWhite";

import returnIcon from "assets/img/icons/return-icon.svg";
import LeadDetail from "./LeadDetail";
import { PAGES_DOMAIN_URL } from "constants";

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackIconWrap = styled.div``;

const LeadDetailContainer = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;

  margin: 0 0 50px;
  padding: 0 28px;

  display: flex;
  flex-direction: column;

  @media (max-width: ${globalConstants.switchTablet}) {
    margin: 0;
    padding: 0;
  }
`;

const ConfirmDelete = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  width: 100%;
  max-width: 442px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 200;

  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const ConfirmDeleteWrap = styled.div`
  margin-top: 60px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ConfirmDeleteText = styled.div`
  line-height: 130%;
`;

const ConfirmDeleteButtons = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_XLARGE};

  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${css.BUTTONS_GAP};
`;

export default () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  // set page Title
  useEffect(() => {
    document.title = "Lead Detail | Scalelup";
  }, []);

  const leadId = searchParams.get("id") ? searchParams.get("id") : null;
  const from = searchParams.get("from") ? searchParams.get("from") : null;

  // Fetch data
  const GET_LEAD_DETAIL_DATA = gql`
    query GetLeadDetailData($leadId: ID!) {
      leadDetail(leadId: $leadId) {
        id
        created_at
        name
        email
        verified
        ip_address
        location_country
        page {
          id
          name
          code
        }
        responder {
          id
          name
        }
        responder_subscribed
        list {
          id
          name
        }
      }
    }
  `;

  const { data, error } = useQuery(GET_LEAD_DETAIL_DATA, {
    variables: {
      leadId,
    },
    skip: !leadId,
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("error fetching lead detail data", error);
    captureException(error);
  }

  const openPage = (code) => {
    window.open(`${PAGES_DOMAIN_URL}/?code=${code}`, "_blank").focus();
  };

  let returnTo = `/mailing-lists`;

  if (from === "mailing-list") {
    const listId = searchParams.get("listId")
      ? searchParams.get("listId")
      : null;

    returnTo = `/mailing-list?id=${listId}`;
  }

  if (from === "autoresponder") {
    const autoresponderId = searchParams.get("autoresponderId")
      ? searchParams.get("autoresponderId")
      : null;

    returnTo = `/autoresponder?id=${autoresponderId}`;
  }

  // handle Lead delete
  const onDeleteClick = () => {
    setConfirmDelete(true);
  };

  const closeDeleteModal = () => {
    setConfirmDelete(false);
  };

  const DELETE_LEAD = gql`
    mutation DeleteLead($leadId: ID!) {
      deleteLead(leadId: $leadId) {
        resultCode
      }
    }
  `;

  const [deleteLead] = useMutation(DELETE_LEAD);

  const onConfirmDelete = async () => {
    try {
      if (loadingDelete) return;

      setLoadingDelete(true);
      setConfirmDelete(false);

      await deleteLead({
        variables: { leadId },
      });

      setLoadingDelete(false);

      navigate(returnTo);
    } catch (error) {
      console.log("confirmDeleteLead error", error);
      captureException(error);
      setLoadingDelete(false);
      return;
    }
  };

  console.log("data", data);

  return (
    <Layout>
      <LeadDetailContainer>
        <Header>
          <PageTitle>Lead Detail</PageTitle>
          <BackIconWrap>
            <RoundActionIcon
              icon={returnIcon}
              iconSize="16px"
              iconAlt="Return"
              onClick={() => navigate(returnTo)}
            />
          </BackIconWrap>
        </Header>
        {!data && (
          <LoadingBox>
            <BoxLoadingSpinner height="435px" />
          </LoadingBox>
        )}
        {data && (
          <LeadDetail
            data={data}
            onDeleteClick={onDeleteClick}
            loadingDelete={loadingDelete}
            openPage={openPage}
          />
        )}
        {confirmDelete && (
          <ModalWrap>
            <Backdrop
              onClick={closeDeleteModal}
              background="rgba(0, 0, 0, 0.7)"
            />
            <ConfirmDelete>
              <ModalWindow
                title="Delete Lead"
                handleModalClose={closeDeleteModal}
                fullScreenHeight={true}
              >
                <ConfirmDeleteWrap>
                  <ConfirmDeleteText>
                    Do you really want to delete the lead?
                  </ConfirmDeleteText>
                  <ConfirmDeleteButtons>
                    <ButtonWhite onClick={closeDeleteModal}>Cancel</ButtonWhite>
                    <ButtonPrimary onClick={onConfirmDelete}>
                      Delete
                    </ButtonPrimary>
                  </ConfirmDeleteButtons>
                </ConfirmDeleteWrap>
              </ModalWindow>
            </ConfirmDelete>
          </ModalWrap>
        )}
      </LeadDetailContainer>
    </Layout>
  );
};
